import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { staffInvitationService } from "../../services/api";
import { toast } from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";

const InviteStaffPage = () => {
  const navigate = useNavigate();
  // No phone field: POST /users/staff-invites/ neither accepts nor stores one
  // (StaffInviteSerializer, users/serializers.py:1634-1638; StaffInviteCreateView,
  // users/views.py:6108-6156). See the T046 note on the form below.
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleType: "",
    staffType: "",
    image: null,
  });
  const [showPositionDropdown, setShowPositionDropdown] = useState(false);
  
  // Permission checks
  const canInviteStaff = usePermission('invite_new_staff');
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchPosition, setSearchPosition] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Role type options (categories)
  const roleTypes = [
    { value: "leadership", label: "Leadership" },
    { value: "coach", label: "Coach" },
    { value: "technical", label: "Technical/Director" },
    { value: "admin", label: "Club Administration" },
    { value: "medical", label: "Medical" },
    { value: "scouting", label: "Scouting/Management" },
    { value: "other", label: "Other" },
  ];

  // Staff type mapping based on role type
  const getStaffTypesForRole = (roleType) => {
    switch (roleType) {
      case "leadership":
        return [
          { value: "ceo", label: "CEO" },
          { value: "president", label: "President" },
          { value: "club_owner", label: "Club Owner" },
          { value: "chairman", label: "Chairman" },
          { value: "managing_director", label: "Managing Director" },
          { value: "general_manager", label: "General Manager" },
        ];
      case "coach":
        return [
          { value: "head_coach", label: "Head Coach" },
          { value: "manager", label: "Manager" },
          { value: "first_team_coach", label: "First Team Coach" },
          { value: "assistant_coach", label: "Assistant Coach" },
          { value: "goalkeeping_coach", label: "Goalkeeping Coach" },
          { value: "youth_coach", label: "Youth Coach" },
          { value: "strength_and_conditioning_coach", label: "Strength & Conditioning Coach" },
        ];
      case "technical":
        return [
          { value: "technical_director", label: "Technical Director" },
          { value: "head_of_football", label: "Head of Football" },
          { value: "director_of_football_methodology", label: "Director of Football Methodology" },
          { value: "sporting_director", label: "Sporting Director" },
          { value: "director_of_football", label: "Director of Football" },
          { value: "head_of_football_operations", label: "Head of Football Operations" },
          { value: "football_director", label: "Football Director" },
          { value: "analysis", label: "Analysis" },
          { value: "operations", label: "Operations" },
          { value: "academy_director", label: "Academy Director" },
          { value: "youth_coordinator", label: "Youth Coordinator" },
        ];
      case "admin":
        return [
          { value: "club_admin", label: "Club Admin" },
          { value: "club_secretary", label: "Club Secretary" },
          { value: "general_secretary", label: "General Secretary" },
          { value: "head_of_administration", label: "Head of Administration" },
          { value: "football_operations_manager", label: "Football Operations Manager" },
          { value: "transfer_manager", label: "Transfer Manager" },
        ];
      case "medical":
        return [
          { value: "medical", label: "Medical" },
          { value: "physiotherapist", label: "Physiotherapist" },
          { value: "sports_doctor", label: "Sports Doctor" },
          { value: "nutritionist", label: "Nutritionist" },
          { value: "sports_psychologist", label: "Sports Psychologist" },
        ];
      case "scouting":
        return [
          { value: "team_manager", label: "Team Manager" },
          { value: "scout", label: "Scout" },
          { value: "head_of_scouting", label: "Head of Scouting" },
          { value: "head_of_recruitment", label: "Head of Recruitment" },
          { value: "chief_scout", label: "Chief Scout" },
          { value: "head_of_analysis", label: "Head of Analysis" },
          { value: "head_of_recruitment_analysis", label: "Head of Recruitment Analysis" },
          { value: "head_of_player_identification", label: "Head of Player Identification" },
          { value: "head_of_football_analytics", label: "Head of Football Analytics" },
          { value: "head_of_data", label: "Head of Data" },
          { value: "recruitment_director", label: "Recruitment Director" },
          { value: "analyst", label: "Analyst" },
          { value: "performance_analyst", label: "Performance Analyst" },
          { value: "video_analyst", label: "Video Analyst" },
          { value: "data_analyst", label: "Data Analyst" },
          { value: "recruitment_analyst", label: "Recruitment Analyst" },
          { value: "opposition_analyst", label: "Opposition Analyst" },
          { value: "youth_scout", label: "Youth Scout" },
        ];
      case "other":
        return [{ value: "other", label: "Other" }];
      default:
        return [];
    }
  };

  // Position categories filter chips
  const positionCategories = [
    { id: "All", label: "All" },
    { id: "leadership", label: "Leadership" },
    { id: "coach", label: "Coach" },
    { id: "technical", label: "Technical/Director" },
    { id: "admin", label: "Club Administration" },
    { id: "medical", label: "Medical" },
    { id: "scouting", label: "Scouting/Management" },
    { id: "other", label: "Other" },
  ];

  // Map UI selection (category + staff_type value) to canonical API payload
  const mapToCanonical = (category, staffValue) => {
    // Leadership category → CEO group
    if (category === "leadership") {
      return { role_type: staffValue, staff_type: "ceo" };
    }
    // Admin category → Club Admin group
    if (category === "admin") {
      return { role_type: staffValue, staff_type: "club_admin" };
    }
    if (category === "coach") {
      if ([
        "head_coach", "manager", "first_team_coach",
        "assistant_coach", "goalkeeping_coach", "strength_and_conditioning_coach",
      ].includes(staffValue)) {
        return { role_type: staffValue, staff_type: "coach" };
      }
      if (staffValue === "youth_coach") return { role_type: "youth_coach", staff_type: "academy" };
    }
    if (category === "technical") {
      if (["technical_director", "head_of_football", "director_of_football_methodology"].includes(staffValue)) {
        return { role_type: staffValue, staff_type: "technical_director" };
      }
      if (["sporting_director", "director_of_football", "head_of_football_operations", "football_director"].includes(staffValue)) {
        return { role_type: staffValue, staff_type: "sporting_director" };
      }
      if (staffValue === "analysis") return { role_type: "performance_analyst", staff_type: "analysis" };
      if (staffValue === "academy_director") return { role_type: "academy_director", staff_type: "academy" };
      if (staffValue === "youth_coordinator") return { role_type: "youth_coordinator", staff_type: "academy" };
      if (staffValue === "operations") return { role_type: "other", staff_type: "other" };
    }
    if (category === "medical") {
      if (staffValue === "medical") return { role_type: "medical_staff", staff_type: "medical" };
      if (staffValue === "physiotherapist") return { role_type: "physiotherapist", staff_type: "medical" };
      if (staffValue === "sports_doctor") return { role_type: "sports_doctor", staff_type: "medical" };
      if (staffValue === "nutritionist") return { role_type: "nutritionist", staff_type: "medical" };
      if (staffValue === "sports_psychologist") return { role_type: "sports_psychologist", staff_type: "medical" };
    }
    if (category === "scouting") {
      if (staffValue === "team_manager") return { role_type: "team_manager", staff_type: "team_manager" };
      if (["scout", "analyst", "performance_analyst", "video_analyst", "data_analyst", "recruitment_analyst", "opposition_analyst"].includes(staffValue)) {
        return { role_type: staffValue, staff_type: "scout" };
      }
      if (["head_of_scouting", "head_of_recruitment", "chief_scout", "head_of_analysis", "head_of_recruitment_analysis", "head_of_player_identification", "head_of_football_analytics", "head_of_data", "recruitment_director"].includes(staffValue)) {
        return { role_type: staffValue, staff_type: "head_of_scouting" };
      }
      if (staffValue === "youth_scout") return { role_type: "youth_scout", staff_type: "academy" };
    }
    if (category === "other") {
      return { role_type: "other", staff_type: "other" };
    }
    return { role_type: "other", staff_type: "other" };
  };

  // Create positions array from role types and staff types (augmented with canonical mapping)
  const positions = roleTypes.flatMap((roleType) => {
    const staffTypes = getStaffTypesForRole(roleType.value);
    return staffTypes.map((staffType) => {
      const canonical = mapToCanonical(roleType.value, staffType.value);
      return {
        name: staffType.label,
        category: roleType.value,
        roleType: canonical.role_type, // canonical role_type to send in API
        staffType: canonical.staff_type, // for display/selection only
      };
    });
  });

  const filteredPositions = positions.filter((position) => {
    const matchesCategory =
      selectedCategory === "All" || position.category === selectedCategory;
    const matchesSearch = position.name
      .toLowerCase()
      .includes(searchPosition.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Match on roleType (unique per title) — not staffType, which is the group
  // and is shared across multiple titles (e.g. all CEO-group titles share
  // staffType='ceo', so matching on staffType would highlight all of them).
  const selectedPositionName = formData.roleType
    ? positions.find((p) => p.roleType === formData.roleType)?.name || formData.roleType
    : "";

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePositionSelect = (position) => {
    setFormData((prev) => ({
      ...prev,
      roleType: position.roleType,
      staffType: position.staffType,
    }));
    setShowPositionDropdown(false);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));
    }
  };

  useEffect(() => {
    if (!formData.image) {
      setImagePreview(null);
      return undefined;
    }

    const url = URL.createObjectURL(formData.image);
    setImagePreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [formData.image]);

  const handleSendInvite = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    setIsSubmitting(true);
    try {
      // Form validation
      if (!formData.firstName.trim()) {
        toast.error("First name is required");
        return;
      }
      if (!formData.lastName.trim()) {
        toast.error("Last name is required");
        return;
      }
      if (!formData.email.trim()) {
        toast.error("Email is required");
        return;
      }
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }
      if (!formData.roleType) {
        toast.error("Role type is required");
        return;
      }
      if (!formData.staffType) {
        toast.error("Staff type is required");
        return;
      }

      // Prepare payload (canonical) with correct field names for API
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        role_type: formData.roleType,
      };

      console.log("Sending staff invitation:", payload);

      const response = await staffInvitationService.sendStaffInvitation(payload);
      if (response.success) {
        toast.success("Staff invitation sent successfully");
        navigate(-1);
      } else {
        toast.error(response.error || "Failed to send invitation");
      }
    } catch (error) {
      console.error("Failed to send staff invitation:", error);
      toast.error("Failed to send invitation. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCloseDropdown = () => {
    setShowPositionDropdown(false);
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }));
  };



  return (
    <div className=" bg-white">
      <div className="px-4 sm:px-6 md:px-12 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 ">
          <button
            onClick={() => navigate(-1)}
            className="text-[#202630] hover:text-gray-600"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M12 19L5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-[20px] font-bold text-[#202630]">Invite Staff</h1>
        </div>

        {/* Form */}
  <div className="bg-white rounded-lg w-full py-6">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* First Row - First Name and Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7 mt-2 ">
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  First name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  placeholder="Ex. John"
                  className="w-full h-[40px] px-3 border border-[#E9E9E9] rounded-md text-[16px] placeholder:text-[#00000099] placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-[16px] font-medium text-[#000000] mb-2">
                  Last name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  placeholder="Ex. Smith"
                  className="w-full h-[40px] px-3 border border-[#E9E9E9] rounded-md text-[16px] placeholder:text-[#00000099] placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Second Row - Email.
                T046: the required-marked phone field that used to sit here
                was dead — never validated, never part of the payload, and the
                staff-invite endpoint neither accepts nor stores a phone
                number, so anything typed was discarded. Removed rather than
                left as an unmarked field that silently drops input. */}
            <div className="mb-7">
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Email address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Email address"
                className="w-full h-[40px] px-3 border border-[#E9E9E9] rounded-md text-[16px] placeholder:text-[#00000099] placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Third Row - Position */}
            <div className="mb-7">
              <label className="block text-[16px] font-medium text-[#000000] mb-2">
                Position in the club *
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPositionDropdown(!showPositionDropdown)}
                  className="w-full h-[40px] px-3 border border-[#E9E9E9] rounded-md text-left text-[16px] placeholder:text-[#00000099] placeholder:font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
                >
                  <span
                    className={
                      formData.staffType
                        ? "text-[#000000]"
                        : "text-[#00000099] font-medium"
                    }
                  >
                    {selectedPositionName || "Select staff position in the club"}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 6L8 10L12 6"
                      stroke="#000000"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {showPositionDropdown && (
                  <>
                    <div
                      className="fixed inset-0 bg-black/30 z-30 md:hidden"
                      onClick={handleCloseDropdown}
                    />
                    <div className="fixed inset-x-0 bottom-0 z-40 max-h-[80vh] rounded-t-2xl border border-[#E9E9E9] bg-white p-4 shadow-lg md:absolute md:inset-auto md:z-10 md:w-full md:mt-1 md:rounded-md md:p-0 md:shadow-lg">
                      <div className="hidden md:block">
                        {/* Desktop search wrapper */}
                        <div className="p-3 border-b border-[#E9E9E9]">
                          <div className="relative">
                            <svg
                              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            <input
                              type="text"
                              value={searchPosition}
                              onChange={(e) => setSearchPosition(e.target.value)}
                              placeholder="Search position"
                              className="w-full pl-10 pr-3 py-2 text-sm border border-[#E9E9E9] rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="md:hidden">
                        {/* Mobile header */}
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-base font-semibold text-[#202630]">
                            Select position
                          </span>
                          <button
                            type="button"
                            onClick={handleCloseDropdown}
                            className="text-sm font-medium text-[#2283FF]"
                          >
                            Close
                          </button>
                        </div>
                        <div className="mb-3">
                          <div className="relative">
                            <svg
                              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            <input
                              type="text"
                              value={searchPosition}
                              onChange={(e) => setSearchPosition(e.target.value)}
                              placeholder="Search position"
                              className="w-full rounded-lg border border-[#E9E9E9] bg-[#F8F9FB] pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Category Filters */}
                      <div className="flex gap-1 overflow-x-auto p-2 bg-[#F8F9FB] border-b border-[#E9E9E9] md:overflow-hidden">
                        {positionCategories.map((category) => (
                          <button
                            key={category.id}
                            type="button"
                            onClick={() => setSelectedCategory(category.id)}
                            className={`w-fit whitespace-nowrap px-3 py-2 text-sm rounded-md ${
                              selectedCategory === category.id
                                ? "bg-white text-[#000000] font-medium shadow-sm"
                                : "text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {category.label}
                          </button>
                        ))}
                      </div>

                      {/* Position List */}
                      <div className="max-h-[60vh] overflow-y-auto md:max-h-60">
                        {filteredPositions.map((position, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handlePositionSelect(position)}
                            className="w-full px-3 py-3 text-left text-sm hover:bg-gray-50 flex items-center gap-3 md:py-2"
                          >
                            <div
                              className={`w-4 h-4 rounded-full border ${
                                formData.roleType === position.roleType
                                  ? "border-[#2283FF] bg-[#2283FF]"
                                  : "border-gray-300"
                              }`}
                            >
                              {formData.roleType === position.roleType && (
                                <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                              )}
                            </div>
                            {position.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Upload Image */}
            <div className="mb-10 ">
              <label className="text-[#2283FF] text-[16px] font-normal cursor-pointer hover:underline">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                Upload staff image (optional)
              </label>
              {imagePreview && formData.image && (
                <div className="mt-3 flex items-center gap-3 rounded-lg border border-[#E9E9E9] bg-[#F8F9FB] p-3">
                  <div className="h-12 w-12 overflow-hidden rounded-md bg-white">
                    <img
                      src={imagePreview}
                      alt="Selected staff"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="truncate text-sm font-medium text-[#202630]">
                      {formData.image.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(formData.image.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Remove selected image"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleSendInvite}
                disabled={isSubmitting}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  isSubmitting 
                    ? "bg-gray-400 text-white cursor-not-allowed" 
                    : "bg-[#2283FF] text-[#F5F5F5] hover:bg-blue-600"
                }`}
              >
                {isSubmitting ? "Sending..." : "Send invite"}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 bg-white text-[#1E1E1E] border border-[#E6E6E6] rounded-lg shadow-sm hover:bg-gray-50 font-normal"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteStaffPage;
